/**
 * The Mail search box (logic/mailQuery, MailSearchViewModel): filters as chips, the text after them, and the Search
 * screen's suggestion menu under it. A chip's press changes how it matches (like, =, ≠). Focusing the empty box lists my
 * recent searches with the filter keys underneath; while typing, the ones that fit sit over the suggestions.
 */
import { MailQuery, type MailChip, type MailChipMode, type MailRecentRow, type MailSearchViewModel } from "@bundle/core";
import { Button, Chip, Divider, Icon, IconButton, Popover, Pressable, SearchChipField, SearchSuggestMenu, Stack, Text, useT, useViewModel, type SearchChipItem } from "@bundle/ui";

const MODE_MARK: Record<MailChipMode, string> = { like: "like", exact: "=", exclude: "≠" };

/** The keys the empty box teaches. */
const FOOTER_KEYS = ["from:", "to:", "mailbox:", "in:", "has:", "is:", "after:", "before:"];

function chipLabel(c: MailChip): string {
  return MailQuery.MAIL_MATCH_KEYS.includes(c.key) ? `${c.key}: ${MODE_MARK[MailQuery.mailChipMode(c)]} ${c.value}` : `${c.key}: ${c.value}`;
}

function RecentRow({ vm, row, active, testID }: { vm: MailSearchViewModel; row: MailRecentRow; active: boolean; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" gap="1" paddingX="2" background={active ? "bg.highlight" : undefined} hoverHighlight testID={`${testID}-row`}>
      <Stack grow>
        <Pressable bare direction="horizontal" onPress={() => vm.chooseRecent(row.id)} accessibilityLabel={row.query} testID={`${testID}-open`}>
          <Stack grow direction="horizontal" align="center" gap="2" paddingY="1.5">
            <Icon name="clock" size="icon.sm" color="text.tertiary" />
            {row.chips.map((c) => (
              <Chip key={c} label={c} />
            ))}
            <Stack grow>
              <Text lines={1}>{row.text}</Text>
            </Stack>
          </Stack>
        </Pressable>
      </Stack>
      <IconButton icon="close" size="sm" accessibilityLabel={t("mail.recent.remove")} onPress={() => vm.removeRecent(row.id)} testID={`${testID}-remove`} />
    </Stack>
  );
}

export function MailSearchBox({ vm, placeholder, testID }: { vm: MailSearchViewModel; placeholder: string; testID: string }) {
  const t = useT();
  const s = useViewModel(vm);
  const modes: { mode: MailChipMode; label: string }[] = [
    { mode: "like", label: t("mail.search.mode.like") },
    { mode: "exact", label: t("mail.search.mode.exact") },
    { mode: "exclude", label: t("mail.search.mode.exclude") },
  ];
  const chips = s.chips.map((c, i): SearchChipItem => {
    const label = chipLabel(c);
    const item: SearchChipItem = { id: String(i), label, removeLabel: t("mail.search.chip.remove", { filter: label }), onRemove: () => vm.removeChip(i) };
    if (s.ors[i] === true) item.joiner = t("mail.search.chip.or");
    if (c.not === true) item.tone = "danger";
    if (MailQuery.MAIL_MATCH_KEYS.includes(c.key)) {
      const current = MailQuery.mailChipMode(c);
      item.menu = modes.map((m) => ({ id: m.mode, label: `${MODE_MARK[m.mode]}  ${m.label}`, selected: m.mode === current, onSelect: () => vm.setChipMode(i, m.mode) }));
    }
    return item;
  });
  const key = s.suggest?.key;
  const title = key === "from" || key === "to" || key === "with" ? t("mail.search.suggest.people") : key === "domain" ? t("mail.search.suggest.domains") : key === "in" || key === "label" ? t("mail.search.suggest.folders") : undefined;
  const empty = s.query.trim() === "";
  return (
    <Popover
      open={s.suggest !== undefined || s.recentOpen}
      onClose={() => vm.dismissSuggest()}
      side="bottom"
      align="start"
      fill
      width="anchor"
      autoFocus={false}
      anchor={
        <SearchChipField
          chips={chips}
          value={s.draft}
          onChange={(text) => vm.setDraft(text)}
          onCaret={(caret) => vm.setCaret(caret)}
          onFocus={() => vm.focus()}
          onKey={(k) => vm.key(k)}
          placeholder={placeholder}
          accessibilityLabel={placeholder}
          testID={testID}
        />
      }
    >
      {s.recentOpen ? (
        <Stack gap="0.5" testID={`${testID}-recent`}>
          <Stack direction="horizontal" align="center" justify="between" paddingX="2" paddingY="1">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("mail.recent.title")}
            </Text>
            {s.recent.length > 0 && empty ? <Button size="sm" variant="ghost" label={t("mail.recent.clear")} onPress={() => vm.clearRecent()} testID={`${testID}-recent-clear`} /> : null}
          </Stack>
          {s.recent.map((r, i) => (
            <RecentRow key={r.id} vm={vm} row={r} active={i === s.recentActive} testID={`${testID}-recent-${i}`} />
          ))}
          {s.recent.length === 0 ? (
            <Stack paddingX="2" paddingY="2">
              <Text color="text.secondary">{t("mail.recent.empty")}</Text>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {s.suggest === undefined ? null : (
        <SearchSuggestMenu
          menu={s.suggest}
          {...(title === undefined ? {} : { title })}
          onChoose={(i) => vm.chooseSuggest(i)}
          onHover={(i) => vm.highlightSuggest(i)}
          testID={`${testID}-suggest`}
        />
      )}
      {s.recentOpen && empty ? (
        <Stack gap="1">
          <Divider />
          <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" wrap testID={`${testID}-keys`}>
            <Text variant="caption" color="text.tertiary">
              {t("mail.recent.filterBy")}
            </Text>
            {FOOTER_KEYS.map((k) => (
              <Chip key={k} label={k} onPress={() => vm.insertKey(k)} testID={`${testID}-key-${k.slice(0, -1)}`} />
            ))}
            <Stack grow />
            <Button size="sm" variant="ghost" label={t("mail.recent.close")} onPress={() => vm.dismissSuggest()} testID={`${testID}-recent-close`} />
          </Stack>
        </Stack>
      ) : null}
    </Popover>
  );
}
